// Top-level Hibajelentések screen: every maintenance request the user can
// see across their tenancies (landlord and tenant side), filterable by
// status and property. #/maintenance is the list, #/maintenance/:id the
// deep-linkable detail (status workflow, photos, comment thread). The
// per-tenancy / per-property tabs keep working as contextual shortcuts;
// this screen is the canonical hub.

import { Component, esc } from '../core/component.js';
import { api, ApiError } from '../core/api.js';
import { auth } from '../core/auth.js';
import { icon } from '../core/icons.js';
import { t } from '../core/i18n.js';
import { formatDateTime } from '../core/format.js';
import { showModal } from '../components/Modal.js';
import { toast } from '../components/Toast.js';
import { showFormError, fieldFromApiError } from '../core/formError.js';

const CATS = ['PLUMBING', 'ELECTRICAL', 'HEATING', 'APPLIANCE', 'STRUCTURAL', 'PEST', 'OTHER'];
const PRIOS = ['LOW', 'NORMAL', 'HIGH', 'URGENT'];
// Chip key → `status` query param ('' = no filter).
const FILTERS = [
  ['active', 'active'],
  ['all', ''],
  ['OPEN', 'OPEN'],
  ['IN_PROGRESS', 'IN_PROGRESS'],
  ['RESOLVED', 'RESOLVED'],
  ['CANCELLED', 'CANCELLED'],
];

export class MaintenanceView extends Component {
  constructor(ctx) {
    super();
    this.id = (ctx && ctx.params && ctx.params.id) || null;
    this._photoUrls = {};
    this.state = this.id
      ? { detail: null, tenancy: null, error: null, busy: false }
      : { items: null, filter: 'active', propertyId: '', tenancies: null, error: null };
  }

  // ---- shared helpers -----------------------------------------------------

  _statusClass(status) {
    if (status === 'RESOLVED') return 'badge-success';
    if (status === 'CANCELLED') return 'badge-muted';
    if (status === 'OPEN') return 'badge-warning';
    return 'badge';
  }

  async _loadProtectedAsset(path) {
    if (this._photoUrls[path]) return this._photoUrls[path];
    try {
      const blob = await api.get(path, { responseType: 'blob' });
      const url = URL.createObjectURL(blob);
      this._photoUrls[path] = url;
      return url;
    } catch (_) {
      return null;
    }
  }

  _hydratePhotos() {
    this.el.querySelectorAll('img[data-protected-src]').forEach(async (img) => {
      const url = await this._loadProtectedAsset(img.getAttribute('data-protected-src'));
      if (url) img.src = url;
    });
  }

  unmount() {
    for (const url of Object.values(this._photoUrls)) {
      try { URL.revokeObjectURL(url); } catch (_) {}
    }
    this._photoUrls = {};
  }

  // ---- templates ----------------------------------------------------------

  template() {
    return this.id ? this._detailTemplate() : this._listTemplate();
  }

  _listTemplate() {
    const items = this.state.items;
    return `
      <section class="stack">
        <div class="row-between">
          <h2>${esc(t('maintenance.title'))}</h2>
          <button class="btn btn-primary" data-action="new">${icon('plus', { size: 'sm' })}<span>${esc(t('tenancy.maintenance.new'))}</span></button>
        </div>
        ${this.state.error ? `<div class="error-banner">${esc(this.state.error)}</div>` : ''}
        <div class="row" style="gap:var(--space-2);flex-wrap:wrap">
          ${FILTERS.map(([key]) => `
            <button class="btn btn-sm ${this.state.filter === key ? 'btn-primary' : 'btn-ghost'}"
                    data-filter="${esc(key)}">${esc(t('maintenance.filters.' + key))}</button>
          `).join('')}
          ${this._propertyFilterHtml()}
        </div>
        ${items === null
          ? `<div class="muted">${esc(t('tenancy.maintenance.loading'))}</div>`
          : items.length === 0
            ? `<div class="empty">${esc(t('maintenance.empty'))}</div>`
            : `<div class="list">${items.map((r) => this._rowHtml(r)).join('')}</div>`}
      </section>
    `;
  }

  _propertyFilterHtml() {
    const items = this.state.items;
    if (!Array.isArray(items)) return '';
    const seen = new Map();
    for (const r of items) {
      if (r.property_id && !seen.has(r.property_id)) seen.set(r.property_id, r.property_name || '');
    }
    if (seen.size < 2) return '';
    const opts = [`<option value="">${esc(t('maintenance.all_properties'))}</option>`]
      .concat([...seen.entries()].map(([id, name]) =>
        `<option value="${esc(id)}" ${this.state.propertyId === id ? 'selected' : ''}>${esc(name)}</option>`));
    return `<select class="select" style="max-width:220px" data-property-filter>${opts.join('')}</select>`;
  }

  _rowHtml(r) {
    return `
      <a class="list-item" href="#/maintenance/${esc(r.id)}">
        <div class="stack-2 grow">
          <strong>${esc(r.title)}</strong>
          <span class="faint">
            ${esc(t('tenancy.maintenance.category.' + r.category))} · ${esc(t('tenancy.maintenance.priority.' + r.priority))}${r.property_name ? ` · ${esc(r.property_name)}` : ''}${r.unit_identifier ? ` · ${esc(r.unit_identifier)}` : ''}
          </span>
          <span class="faint">${esc(t('tenancy.maintenance.reported_by', { name: r.reporter_name || r.reporter_email || '' }))} · ${esc(formatDateTime(r.created_at))}</span>
        </div>
        <span class="badge ${this._statusClass(r.status)}">${esc(t('tenancy.maintenance.status.' + r.status))}</span>
      </a>
    `;
  }

  _detailTemplate() {
    const d = this.state.detail;
    if (this.state.error) {
      return `
        <section class="stack">
          <a href="#/maintenance" class="back-link">${icon('arrow-left', { size: 'sm' })}<span>${esc(t('maintenance.back'))}</span></a>
          <div class="error-banner">${esc(this.state.error)}</div>
        </section>`;
    }
    if (!d) return `<section class="stack"><div class="muted">${esc(t('tenancy.maintenance.loading'))}</div></section>`;
    const terminal = d.status === 'RESOLVED' || d.status === 'CANCELLED';
    const isLandlord = this.state.tenancy && String(this.state.tenancy.landlord_user_id) === String(auth.user && auth.user.id);
    const comments = Array.isArray(d.comments) ? d.comments : [];
    return `
      <section class="stack">
        <a href="#/maintenance" class="back-link">${icon('arrow-left', { size: 'sm' })}<span>${esc(t('maintenance.back'))}</span></a>
        <div class="card stack">
          <div class="row" style="gap:var(--space-2);align-items:center;flex-wrap:wrap">
            <span class="badge ${this._statusClass(d.status)}">${esc(t('tenancy.maintenance.status.' + d.status))}</span>
            <h2 class="grow" style="margin:0">${esc(d.title)}</h2>
          </div>
          <span class="faint">
            ${esc(t('tenancy.maintenance.category.' + d.category))} · ${esc(t('tenancy.maintenance.priority.' + d.priority))}${d.unit_identifier ? ` · ${esc(d.unit_identifier)}` : ''}
          </span>
          <span class="faint">${esc(t('tenancy.maintenance.reported_by', { name: d.reporter_name || d.reporter_email || '' }))} · ${esc(formatDateTime(d.created_at))}</span>
          ${d.description ? `<div>${esc(d.description)}</div>` : ''}
          ${isLandlord && !terminal ? `
            <div class="row" style="gap:var(--space-2);flex-wrap:wrap">
              <span class="faint">${esc(t('tenancy.maintenance.set_status'))}:</span>
              ${['OPEN', 'IN_PROGRESS', 'RESOLVED', 'CANCELLED'].filter((s) => s !== d.status).map((s) => `
                <button class="btn btn-sm btn-ghost" data-action="status" data-status="${s}" ${this.state.busy ? 'disabled' : ''}>${esc(t('tenancy.maintenance.status.' + s))}</button>
              `).join('')}
            </div>` : ''}
        </div>
        ${this._photosHtml(d, isLandlord, terminal)}
        <div class="card stack">
          <h3 style="margin:0">${esc(t('tenancy.maintenance.comments_title'))}</h3>
          ${comments.length
            ? `<div class="stack">${comments.map((cm) => `
                <div class="stack-2" style="border-top:1px solid var(--color-border);padding-top:var(--space-2)">
                  <span class="faint">${esc(cm.author_name || cm.author_email || '')} · ${esc(formatDateTime(cm.created_at))}</span>
                  <div>${esc(cm.body)}</div>
                </div>`).join('')}</div>`
            : `<div class="muted">${esc(t('tenancy.maintenance.no_comments'))}</div>`}
          <form class="row" style="gap:var(--space-2)" data-comment-form>
            <textarea class="input grow" name="body" rows="2" placeholder="${esc(t('tenancy.maintenance.comment_placeholder'))}"></textarea>
            <button type="submit" class="btn btn-primary" ${this.state.busy ? 'disabled' : ''}>${esc(t('tenancy.maintenance.comment_send'))}</button>
          </form>
        </div>
      </section>
    `;
  }

  _photosHtml(d, isLandlord, terminal) {
    const photos = Array.isArray(d.photos) ? d.photos : [];
    if (photos.length === 0 && terminal) return '';
    const tiles = photos.map((p) => {
      const canDelete = !terminal && (isLandlord || (auth.user && p.uploader_user_id === auth.user.id));
      return `
        <div class="room-photo-tile">
          <button type="button" class="defect-photo-trigger" data-action="photo-view" data-photo="${esc(p.id)}" aria-label="${esc(t('tenancy.maintenance.photos.view'))}">
            <img alt="${esc(t('tenancy.maintenance.photos.photo'))}" data-protected-src="/maintenance-photos/${esc(p.id)}/photo" />
            <span class="defect-photo-trigger-overlay">${icon('eye', { size: 'sm' })}</span>
          </button>
          ${canDelete ? `<button type="button" class="btn btn-ghost btn-icon room-photo-delete" data-action="photo-delete" data-photo="${esc(p.id)}" aria-label="${esc(t('common.delete'))}">${icon('trash', { size: 'sm' })}</button>` : ''}
        </div>`;
    }).join('');
    const adder = !terminal ? `
      <label class="room-photo-add" title="${esc(t('tenancy.maintenance.photos.add'))}">
        ${icon('upload', { size: 'sm' })}
        <span>${esc(t('tenancy.maintenance.photos.add'))}</span>
        <input type="file" accept="image/*" hidden data-photo-input />
      </label>` : '';
    return `
      <div class="card stack">
        <div class="room-photos-head">
          <strong>${esc(t('tenancy.maintenance.photos.title'))}</strong>
          <span class="faint">${esc(t('tenancy.maintenance.photos.hint'))}</span>
        </div>
        <div class="room-photo-grid">${tiles}${adder}</div>
      </div>`;
  }

  // ---- wiring --------------------------------------------------------------

  mount() {
    if (this.id) this._mountDetail();
    else this._mountList();
  }

  _mountList() {
    this.el.querySelectorAll('[data-filter]').forEach((b) =>
      b.addEventListener('click', () => {
        this.setState({ filter: b.getAttribute('data-filter'), items: null });
        this._loadList();
      }));
    this.el.querySelector('[data-property-filter]')?.addEventListener('change', (e) => {
      this.setState({ propertyId: e.currentTarget.value, items: null });
      this._loadList();
    });
    this.el.querySelector('[data-action="new"]')?.addEventListener('click', () => this._openCreateModal());
    if (this.state.items === null) this._loadList();
  }

  _mountDetail() {
    this.el.querySelectorAll('[data-action="status"]').forEach((b) =>
      b.addEventListener('click', () => this._changeStatus(b.getAttribute('data-status'))));
    this.el.querySelector('[data-comment-form]')?.addEventListener('submit', (e) => {
      e.preventDefault();
      this._addComment(e.target);
    });
    this.el.querySelectorAll('[data-action="photo-view"]').forEach((b) =>
      b.addEventListener('click', () => this._viewPhoto(b.getAttribute('data-photo'))));
    this.el.querySelectorAll('[data-action="photo-delete"]').forEach((b) =>
      b.addEventListener('click', () => this._deletePhoto(b.getAttribute('data-photo'))));
    const input = this.el.querySelector('[data-photo-input]');
    if (input) input.addEventListener('change', () => {
      const file = input.files && input.files[0];
      if (file) this._uploadPhoto(file);
    });
    if (this.state.detail === null) this._loadDetail();
    else this._hydratePhotos();
  }

  async _loadList() {
    try {
      const q = new URLSearchParams();
      const filter = FILTERS.find(([k]) => k === this.state.filter);
      if (filter && filter[1]) q.set('status', filter[1]);
      if (this.state.propertyId) q.set('property_id', this.state.propertyId);
      const res = await api.get('/maintenance' + (q.toString() ? '?' + q.toString() : ''));
      // Keep the property options stable across reloads: merge ids seen so far.
      const items = res.data || [];
      this.setState({ items, error: null });
    } catch (err) {
      this.setState({ items: [], error: err instanceof ApiError ? err.message : t('tenancy.maintenance.errors.load') });
    }
  }

  async _loadDetail() {
    try {
      const detail = await api.get(`/maintenance/${this.id}`);
      let tenancy = null;
      try { tenancy = await api.get(`/tenancies/${detail.tenancy_id}`); } catch (_) {}
      this.setState({ detail, tenancy, error: null, busy: false });
      this._hydratePhotos();
    } catch (err) {
      this.setState({ error: err instanceof ApiError ? err.message : t('tenancy.maintenance.errors.load'), busy: false });
    }
  }

  async _changeStatus(status) {
    if (this.state.busy) return;
    this.setState({ busy: true });
    try {
      await api.post(`/maintenance/${this.id}/status`, { status });
      toast.success(t('tenancy.maintenance.status.' + status));
      this.setState({ detail: null });
      this._loadDetail();
    } catch (err) {
      this.setState({ busy: false });
      toast.error(err instanceof ApiError ? err.message : t('tenancy.maintenance.errors.status'));
    }
  }

  async _addComment(form) {
    if (this.state.busy) return;
    const body = (form.querySelector('[name=body]')?.value || '').trim();
    if (!body) return;
    this.setState({ busy: true });
    try {
      await api.post(`/maintenance/${this.id}/comments`, { body });
      this.setState({ detail: null });
      this._loadDetail();
    } catch (err) {
      this.setState({ busy: false });
      toast.error(err instanceof ApiError ? err.message : t('tenancy.maintenance.errors.comment'));
    }
  }

  async _uploadPhoto(file) {
    if (this.state.busy) return;
    this.setState({ busy: true });
    try {
      const fd = new FormData();
      fd.append('photo', file);
      await api.post(`/maintenance/${this.id}/photos`, fd);
      this.setState({ detail: null });
      this._loadDetail();
    } catch (err) {
      this.setState({ busy: false });
      toast.error(err instanceof ApiError ? err.message : t('tenancy.maintenance.errors.photo_upload'));
    }
  }

  async _deletePhoto(photoId) {
    if (!confirm(t('tenancy.maintenance.photos.delete_confirm'))) return;
    try {
      await api.delete(`/maintenance-photos/${photoId}`);
      this.setState({ detail: null });
      this._loadDetail();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : t('tenancy.maintenance.errors.photo_delete'));
    }
  }

  async _viewPhoto(photoId) {
    const url = await this._loadProtectedAsset(`/maintenance-photos/${photoId}/photo`);
    if (url) window.open(url, '_blank', 'noopener');
  }

  async _openCreateModal() {
    let tenancies = this.state.tenancies;
    if (tenancies === null) {
      try {
        const res = await api.get('/tenancies');
        tenancies = res.data || [];
      } catch (err) {
        toast.error(err instanceof ApiError ? err.message : t('common.error'));
        return;
      }
      this.setState({ tenancies });
    }
    const usable = (tenancies || []).filter((tn) => tn.status !== 'ENDED');
    if (usable.length === 0) {
      toast.error(t('maintenance.no_tenancy'));
      return;
    }
    const tenancyLabel = (tn) => {
      const units = (tn.units || []).map((u) => u.identifier).filter(Boolean).join(', ');
      const prop = (tn.units && tn.units[0] && tn.units[0].property_name) || '';
      return [prop, units].filter(Boolean).join(' · ') || tn.id;
    };
    const body = `
      <div data-form-error></div>
      <form class="stack" data-form>
        <div class="field">
          <label>${esc(t('maintenance.field_tenancy'))}</label>
          <select class="select" name="tenancy_id" data-tenancy-select>
            ${usable.map((tn) => `<option value="${esc(tn.id)}">${esc(tenancyLabel(tn))}</option>`).join('')}
          </select>
        </div>
        <div class="field" data-unit-field>
          <label>${esc(t('tenancy.maintenance.field_unit'))}</label>
          <select class="select" name="unit_id"></select>
        </div>
        <div class="field"><label>${esc(t('tenancy.maintenance.field_title'))}</label><input class="input" name="title" required maxlength="190" /></div>
        <div class="row" style="gap:var(--space-3)">
          <div class="field grow">
            <label>${esc(t('tenancy.maintenance.field_category'))}</label>
            <select class="select" name="category">${CATS.map((k) => `<option value="${k}">${esc(t('tenancy.maintenance.category.' + k))}</option>`).join('')}</select>
          </div>
          <div class="field grow">
            <label>${esc(t('tenancy.maintenance.field_priority'))}</label>
            <select class="select" name="priority">${PRIOS.map((k) => `<option value="${k}" ${k === 'NORMAL' ? 'selected' : ''}>${esc(t('tenancy.maintenance.priority.' + k))}</option>`).join('')}</select>
          </div>
        </div>
        <div class="field"><label>${esc(t('tenancy.maintenance.field_description'))}</label><textarea class="input" name="description" rows="3"></textarea></div>
      </form>`;
    const syncUnits = (host) => {
      const sel = host.querySelector('[data-tenancy-select]');
      const unitSel = host.querySelector('[name=unit_id]');
      const tn = usable.find((x) => String(x.id) === String(sel.value));
      const units = (tn && tn.units) || [];
      unitSel.innerHTML = `<option value="">${esc(t('tenancy.maintenance.field_unit_none'))}</option>`
        + units.map((u) => `<option value="${esc(u.unit_id)}">${esc(u.identifier || u.unit_id)}</option>`).join('');
    };
    const m = showModal({
      title: t('tenancy.maintenance.new'),
      body,
      actions: [
        { label: t('common.cancel') },
        { label: t('tenancy.maintenance.submit'), variant: 'primary', onClick: async () => {
          const host = m.el.querySelector('[data-modal-body]');
          const data = Object.fromEntries(new FormData(host.querySelector('[data-form]')).entries());
          const tenancyId = data.tenancy_id;
          delete data.tenancy_id;
          if (!data.unit_id) delete data.unit_id;
          try {
            const created = await api.post(`/tenancies/${tenancyId}/maintenance`, data);
            toast.success(t('tenancy.maintenance.new'));
            window.location.hash = `#/maintenance/${created.id}`;
          } catch (e) {
            const message = e instanceof ApiError ? e.message : t('tenancy.maintenance.errors.save');
            const field = e instanceof ApiError ? fieldFromApiError(e, ['title', 'unit_id', 'category', 'priority']) : null;
            showFormError(host, message, { field });
            return false;
          }
        }},
      ],
    });
    const host = m.el.querySelector('[data-modal-body]');
    host.querySelector('[data-tenancy-select]').addEventListener('change', () => syncUnits(host));
    syncUnits(host);
  }
}
